import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { ThemeService } from './theme.service';

/**
 * DarkModeToggleComponent — the shared light/dark switch dropped into every
 * portal's chrome (operator session header, admin topbar, calendar header,
 * customer sidebar). A single icon button: moon in light mode, sun in dark.
 *
 * It inherits `currentColor`, so the icon reads correctly whether it sits on a
 * light topbar or a dark brand band — no per-host theming needed. Delegates all
 * state to the root `ThemeService`; `aria-pressed` reflects the active mode.
 */
@Component({
  selector: 'abx-dark-mode-toggle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="abx-theme-toggle"
      [attr.aria-pressed]="theme.isDark()"
      [attr.aria-label]="label()"
      [title]="label()"
      (click)="theme.toggle()">
      <i class="bx" [class.bx-moon]="!theme.isDark()" [class.bx-sun]="theme.isDark()" aria-hidden="true"></i>
    </button>
  `,
  styles: [
    `
      .abx-theme-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        padding: 0;
        border: none;
        border-radius: var(--radius-pill, 9999px);
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-size: 1.3rem;
        line-height: 1;
        transition: background var(--t-short, 150ms ease-out);
      }
      .abx-theme-toggle:hover {
        background: color-mix(in srgb, currentColor 12%, transparent);
      }
      .abx-theme-toggle:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring, 0 0 0 3px rgba(10, 61, 92, 0.75));
      }
      .abx-theme-toggle .bx {
        pointer-events: none;
      }
    `,
  ],
})
export class DarkModeToggleComponent {
  protected readonly theme = inject(ThemeService);
  protected readonly label = computed(() =>
    this.theme.isDark() ? 'Switch to light mode' : 'Switch to dark mode',
  );
}
